import dayjs, { CHICAGO_TIMEZONE } from "../utils/dayjsTimezone";
import {
  INCIDENT_TYPE_LABEL_MAP,
  normalizeIncidentTypeValue,
} from "../data/Fleet/IncidentType";

export const formatAssetValue = (key: string, value: string) => {
  if (key === "engineType" || key === "trailerType") {
    return value
      .split(/(?=[A-Z])/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  if (key === "state" || key === "vin" || key === "licensePlate") {
    return value.toString().toUpperCase();
  }
  return value;
};

export const formatState = (state: string) => state.toUpperCase();

export const formatPaymentMethod = (paymentMethod: string) => {
  return paymentMethod
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatIncidentType = (incidentType: string) => {
  const normalizedIncidentType = normalizeIncidentTypeValue(incidentType);
  if (!normalizedIncidentType) return "";

  return (
    INCIDENT_TYPE_LABEL_MAP[normalizedIncidentType] ??
    normalizedIncidentType
      .split(/(?=[A-Z])/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
};

export const formatDate = (input: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
  if (m) {
    const [, y, mo, d] = m;
    return `${Number(mo)}/${Number(d)}/${y}`;
  }
  const dt = new Date(input);
  return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
};

export const capitalizeFirstChar = (str: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : null;

export const formatLoanValues = (key: string, value: string) => {
  if (
    key === "loanStartDate" ||
    key === "loanEndDate" ||
    key === "estimatedEndDate"
  ) {
    return formatDate(value);
  }
  if (
    key === "loanAmount" ||
    key === "monthlyPayment" ||
    key === "estimatedRemainingBalance" ||
    key === "latestVerifiedRemainingBalance"
  ) {
    const numberValue = Number(value);
    if (!isNaN(numberValue)) {
      return `$${numberValue.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `$${value}`;
  }
  if (key === "verifiedRemainingBalance") {
    const numberValue = Number(value);
    if (!isNaN(numberValue)) {
      if (numberValue === 0) return "";
      return `$${numberValue.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return value;
  }
  if (key === "interestRate") {
    return `${value}%`;
  }
  if (key === "loanTermMonths") {
    return `${value} months`;
  }
  return value;
};

export const formatRentValues = (
  key: string,
  value: string,
  billingFrequency?: string,
) => {
  if (key === "startDate") {
    return formatDate(value);
  }
  if (key === "endDate") {
    return value !== null ? formatDate(value) : "N/A";
  }
  if (
    key === "baseRentalRate" ||
    key === "mileageRate" ||
    key === "reimbursableDeposit" ||
    key === "mileageCaps" ||
    key === "remainingBalance"
  ) {
    const numberValue = Number(value);
    if (!isNaN(numberValue)) {
      return `$${numberValue.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `$${value}`;
  }
  if (key === "rentalTerm") {
    return `${value} ${
      billingFrequency === "Monthly"
        ? "months"
        : billingFrequency === "Weekly"
          ? "weeks"
          : "years"
    }`;
  }
  if (key === "leaseTerm") {
    return `${value} months`;
  }
  return value;
};

export const formatSaleValues = (key: string, value: string) => {
  if (key === "fairMarketValue") {
    const numberValue = Number(value);
    if (!isNaN(numberValue)) {
      return `$${numberValue.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `$${value}`;
  }
  if (key === "saleDate" || key === "fmvDate") {
    return formatDate(value);
  }
  if (key === "forSale") {
    return value ? "Yes" : "No";
  }
  return value;
};

type FormatterInput =
  | string
  | number
  | Date
  | null
  | undefined
  | { value?: string | number | Date | null | undefined };

export const formatDateTimeInChicago = (
  input: FormatterInput,
  format = "MM/DD/YYYY hh:mm A",
) => {
  const rawValue =
    input && typeof input === "object" && "value" in input
      ? (input as { value?: string | number | Date | null | undefined }).value
      : input;

  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return "";
  }

  const normalizedValue = dayjs.isDayjs(rawValue)
    ? rawValue.toISOString()
    : rawValue instanceof Date
      ? rawValue.toISOString()
      : rawValue;

  const utcDate = dayjs.utc(
    typeof normalizedValue === "object" && normalizedValue !== null
      ? ""
      : normalizedValue,
  );

  if (!utcDate.isValid()) {
    return "";
  }

  return utcDate.tz(CHICAGO_TIMEZONE).format(format);
};
